import * as React from "react";

import {Decimal} from "decimal.js";
import {motion} from "framer-motion";

import * as constants from "@client/constants";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {Delta} from "@client/templates/ClientDashboard";
import {RPCResult, classNames, models, server} from "@reactivated";

export function Table<T, K extends string[]>(props: {
    data: T[];
    dynamicFields?: [...K];
    definition: Array<{
        name: keyof T | K[number];
        header: string;
        showIf?: boolean;
        render: (
            row: T,
            props: {isSubRow: boolean; isExpanded: boolean},
        ) => React.ReactNode;
    }>;
    expanded: (row: T) => T[];
    getSubTableClasses: (row: T) => string;
    getSubRowClasses: (row: T) => string;
}) {
    const [expanded, setExpanded] = React.useState<number | null>(null);
    const definition = props.definition.filter(({showIf}) => showIf == null || showIf);
    const totalColumns = definition.length;

    // https://github.com/w3c/csswg-drafts/issues/865#issuecomment-350585274
    // https://github.com/w3c/csswg-drafts/issues/8286
    // https://uxdesign.cc/position-stuck-96c9f55d9526
    // No native solution for horizontally scrolling tables with sticky headers.
    // Right now we just take up the full height of the container and make the table itself fully scrollable.
    // Real ugly but it works because this is a table that on the app takes up most of the screen anyway.
    // Not as good on the client.
    return (
        <div
            role="table"
            style={{gridTemplateColumns: `repeat(${totalColumns}, max-content)`}}
            className="
                -mx-4 grid min-w-full flex-1 overflow-auto overscroll-x-none text-sm
            "
        >
            <div
                role="row"
                style={{
                    gridColumn: `span ${totalColumns}`,
                }}
                className="
                    sticky top-0 z-10 grid grid-cols-subgrid border-b border-gray-200
                    bg-stone-50
                "
            >
                {definition.map(({name, header}, columnIndex) => (
                    <div
                        role="columnheader"
                        key={name.toString()}
                        className={classNames(
                            "py-2 text-left text-sm font-semibold text-gray-900",
                            columnIndex === 0 ? "pl-0 pr-3" : "px-3",
                        )}
                    >
                        {header}
                    </div>
                ))}
            </div>
            <div
                role="rowgroup"
                style={{gridColumn: `span ${totalColumns}`}}
                className="grid grid-cols-subgrid divide-y divide-gray-200"
            >
                {props.data.map((row, index) => (
                    <React.Fragment key={index}>
                        <button
                            type="button"
                            aria-expanded={index === expanded}
                            role="row"
                            onClick={() =>
                                index === expanded
                                    ? setExpanded(null)
                                    : setExpanded(index)
                            }
                            style={{
                                gridColumn: `span ${totalColumns}`,
                            }}
                            className="grid grid-cols-subgrid text-left"
                        >
                            {definition.map(({name, render}, columnIndex) => (
                                <div
                                    role="cell"
                                    key={name.toString()}
                                    className={classNames(
                                        "whitespace-nowrap py-3.5 text-sm text-gray-800 flex",
                                        columnIndex === 0 ? "pl-0 pr-3" : "px-3",
                                    )}
                                >
                                    {render(row, {
                                        isSubRow: false,
                                        isExpanded: expanded === index,
                                    })}
                                </div>
                            ))}
                        </button>
                        {expanded === index &&
                            props.expanded(row).map((subRow, subIndex) => (
                                <div
                                    role="row"
                                    key={subIndex}
                                    style={{
                                        gridColumn: `span ${totalColumns}`,
                                    }}
                                    className={classNames(
                                        "grid grid-cols-subgrid",
                                        props.getSubRowClasses(subRow),
                                    )}
                                >
                                    {definition.map(({name, render}, columnIndex) => (
                                        <div
                                            role="cell"
                                            key={name.toString()}
                                            className={classNames(
                                                "whitespace-nowrap py-2 text-sm text-gray-700",
                                                columnIndex === 0
                                                    ? "pl-4 pr-3"
                                                    : "px-3",
                                            )}
                                        >
                                            {render(subRow, {
                                                isSubRow: true,
                                                isExpanded: false,
                                            })}
                                        </div>
                                    ))}
                                </div>
                            ))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

export const WeeklyMacros = (props: {
    profile: server.core.rpc.Profile.output;
    result: ReturnType<
        typeof utils.useHMRAsync<
            Promise<RPCResult<models["journal.reports.WeeklyMacros"]>>
        >
    >;
}) => {
    const result = props.result;

    const [showTDEE, setShowTDEE] = React.useState(false);

    if (result.value?.type != "success") {
        return <div>Loading...</div>;
    }

    const numberFormatter = new Intl.NumberFormat("en-US");
    const hasSteps = result.value.data.statuses.some((row) => row.steps != null);
    const hasExpendedCalories = result.value.data.statuses.some(
        (row) => row.expended_calories != null,
    );
    const hasWeight = result.value.data.statuses.some((row) => row.weight != null);
    const hasTDEE =
        showTDEE && result.value.data.statuses.some((row) => row.tdee != null);
    const hasWeightDelta = result.value.data.statuses.some(
        (row) => row.weight_delta != null,
    );

    const metrics = [
        ...constants.CORE_METRICS.filter(([metric]) => metric !== "calories"),
        ...constants.OPTIONAL_METRICS,
    ];

    const data = result.value.data.statuses;

    const table = (
        <Table
            data={data}
            dynamicFields={["weight_delta", "expand"]}
            definition={[
                {
                    name: "expand",
                    header: "",
                    render: (row, {isSubRow, isExpanded}) =>
                        isSubRow == false && (
                            <motion.svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="mt-0.5 -mr-2 ml-2 size-4"
                                initial={{rotate: 90}}
                                animate={{rotate: isExpanded ? 180 : 90}}
                                transition={{duration: 0.2, ease: "easeInOut"}}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m4.5 15.75 7.5-7.5 7.5 7.5"
                                />
                            </motion.svg>
                        ),
                },
                {
                    name: "date",
                    header: "Date",
                    render: (row, {isSubRow}) => (
                        <span
                            className={classNames(
                                "font-medium",
                                isSubRow ? "pl-2 -mr-2" : "",
                            )}
                        >
                            {row.date}
                        </span>
                    ),
                },
                {
                    name: "weight",
                    header: "Weight",
                    showIf: hasWeight,
                    render: (row) =>
                        utils.formatDecimalAndUnit(
                            row.weight != null
                                ? new Decimal(row.weight)
                                      .dividedBy(
                                          props.profile.is_imperial ? "0.45359237" : 1,
                                      )
                                      .toDecimalPlaces(1)
                                : null,
                            "weight",
                            props.profile.is_imperial,
                        ),
                },
                {
                    name: "weight_delta",
                    header: "",
                    showIf: hasWeightDelta,
                    render: (row) =>
                        row.weight_delta != null ? (
                            <Delta
                                valueAndType={{
                                    value: new Decimal(row.weight_delta)
                                        .dividedBy(
                                            props.profile.is_imperial
                                                ? "0.45359237"
                                                : 1,
                                        )
                                        .toString(),
                                    type: "weight",
                                }}
                                isImperial={props.profile.is_imperial}
                            />
                        ) : null,
                },
                {
                    name: "tdee",
                    header: "TDEE",
                    showIf: hasTDEE,
                    render: (row) =>
                        utils.formatDecimalAndUnit(
                            row.tdee != null
                                ? new Decimal(row.tdee)
                                      .dividedBy(
                                          props.profile.use_kilojoules
                                              ? constants.KILOJOULES_MULTIPLIER
                                              : 1,
                                      )
                                      .toDecimalPlaces(0)
                                : null,
                            "cal",
                            props.profile.use_kilojoules,
                        ),
                },
                {
                    name: "steps",
                    header: "Steps",
                    showIf: hasSteps,
                    render: (row) =>
                        row.steps != null
                            ? numberFormatter.format(
                                  new Decimal(row.steps).toDecimalPlaces(0).toNumber(),
                              )
                            : "-",
                },
                {
                    name: "calories",
                    header: "Calories",
                    render: (row) =>
                        utils.formatDecimalAndUnit(
                            row.calories != null
                                ? new Decimal(row.calories)
                                      .dividedBy(
                                          props.profile.use_kilojoules
                                              ? constants.KILOJOULES_MULTIPLIER
                                              : 1,
                                      )
                                      .toDecimalPlaces(0)
                                : null,
                            "cal",
                            props.profile.use_kilojoules,
                        ),
                },
                {
                    name: "expended_calories",
                    header: "Exercise",
                    showIf: hasExpendedCalories,
                    render: (row) =>
                        utils.formatDecimalAndUnit(
                            row.expended_calories != null
                                ? new Decimal(row.expended_calories)
                                      .dividedBy(
                                          props.profile.use_kilojoules
                                              ? constants.KILOJOULES_MULTIPLIER
                                              : 1,
                                      )
                                      .toDecimalPlaces(0)
                                : null,
                            "cal",
                            props.profile.use_kilojoules,
                        ),
                },
                ...metrics.map(([metric, , , unit, label]) => ({
                    name: metric,
                    header: label,
                    render: (row: models["journal.reports.WeeklyMacrosEntry"]) => {
                        return utils.formatDecimalAndUnit(
                            row[metric],
                            unit,
                            props.profile.is_imperial,
                        );
                    },
                })),
            ]}
            expanded={(row) => {
                return row.days.map((d) => ({...d, days: []}));
            }}
            getSubTableClasses={() => ""}
            getSubRowClasses={(row) =>
                row.include_in_reports == false ? "line-through" : ""
            }
        />
    );

    const averageTDEE = result.value.data.average_tdee;

    return (
        <div className="flex h-full flex-col">
            {averageTDEE != null && (
                <div className="mb-4 space-y-4">
                    <p>
                        <button
                            onClick={() => setShowTDEE(!showTDEE)}
                            className="
                                inline-flex items-center gap-0.5 font-semibold
                                text-indigo-700
                            "
                        >
                            TDEE
                            <typography.Icon icon="0856-question-circle" />
                        </button>{" "}
                        over the last 12 weeks:{" "}
                        <strong>
                            {utils.formatDecimalAndUnit(
                                new Decimal(averageTDEE)
                                    .dividedBy(
                                        props.profile.use_kilojoules
                                            ? constants.KILOJOULES_MULTIPLIER
                                            : 1,
                                    )
                                    .toDecimalPlaces(0),
                                "cal",
                                props.profile.use_kilojoules,
                            )}
                        </strong>
                    </p>
                    {showTDEE && (
                        <>
                            <p>
                                We estimated your Total Daily Energy Expenditure (TDEE)
                                based on your calories consumed and weight changes each
                                week.
                            </p>
                            <p>Then we take the average of the last 4 weeks.</p>
                        </>
                    )}
                </div>
            )}
            {table}
        </div>
    );
};
